import { createSelector } from '@reduxjs/toolkit';
import { memoize } from 'lodash-es';

import route from '@/lib/route';
import { AppState } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';
import {
  createSelectCanAccessHabitat,
  selectDayPassHabitatsHistory,
  selectSupportedHabitatsHistory,
} from '@/redux/selectors/users';
import store from '@/redux/store';

import { setMembersOnlyWarningModalState } from '../MembersOnlyWarningModal/redux/actions';
import { setProductsPromptModalState } from '../products/ProductsPromptModal/redux/actions';

export const buildHabitatURL = (zooSlug: string, habitatSlug: string, query?: string) =>
  `/h/${zooSlug}/${habitatSlug}${query ? `?${query}` : ''}`;

const isActive = <K extends string | number | symbol>(map: Record<K, string> | undefined, key: K) => {
  if (!map?.[key]) {
    return false;
  }
  return Date.parse(map[key]) > Date.now();
};

export const selectHabitatForProductsPromptModal = createSelector(
  createSortedHabitatsSelector(['online', 'lastMediaCapture', 'index', 'currentViewers', 'title']),
  (state: AppState) => state.user.subscription.habitatsAccessUntil,
  (habitats, habitatsAccessUntil) => {
    return habitats.find(
      (habitat) => !(habitatsAccessUntil?.[habitat._id] && Date.parse(habitatsAccessUntil[habitat._id]) > Date.now()),
    );
  },
);

export const createSelectProductsPromptModalState = memoize((habitatId: string | undefined) =>
  createSelector(
    selectDayPassHabitatsHistory,
    selectSupportedHabitatsHistory,
    (state: AppState) => state.user.persona,
    (state: AppState) => state.user.subscription?.featuresUntil,
    (state: AppState) => state.user.subscription?.habitatsAccessUntil,
    (
      dayPassHabitatsHistory,
      supportedHabitatsHistory,
      persona,
      featuresUntil,
      habitatsAccessUntil,
    ): Parameters<typeof setProductsPromptModalState>[0] => {
      const isEducator = persona === 'educator';
      const isExplorerFeatureExpired = featuresUntil?.explorer && !isActive(featuresUntil, 'explorer');

      if (!habitatId) {
        return {
          primaryProductType: 'all-habitats-day-pass',
          secondaryProductType: isExplorerFeatureExpired ? 'explorer-pass' : undefined,
          forEducator: isEducator,
        };
      }

      // If user has active day pass or supported habitat, show the explorer pass
      const hasActiveHabitatAccess = Object.values(habitatsAccessUntil ?? {}).some(
        (value) => Date.parse(value) > Date.now(),
      );
      if (hasActiveHabitatAccess) {
        return isEducator
          ? {
              habitatId,
              primaryProductType: 'single-habitat-day-pass',
              secondaryProductType: 'educator-pass',
              forEducator: true,
            }
          : {
              habitatId,
              primaryProductType: 'explorer-pass',
              secondaryProductType: 'single-habitat-day-pass',
            };
      }

      // If the explorer feature was active but is now expired, show the explorer pass
      if (isExplorerFeatureExpired) {
        return isEducator
          ? {
              primaryProductType: 'educator-pass',
              secondaryProductType: 'all-habitats-day-pass',
              forEducator: true,
            }
          : {
              habitatId,
              primaryProductType: 'all-habitats-day-pass',
              secondaryProductType: 'explorer-pass',
            };
      }

      // If the habitat was accessible by day pass, show the sponsorship and single habitat day pass
      if (!isEducator && (dayPassHabitatsHistory.has(habitatId) || supportedHabitatsHistory.has(habitatId))) {
        return {
          habitatId,
          primaryProductType: 'sponsorship',
          secondaryProductType: 'single-habitat-day-pass',
        };
      }

      // default to all habitats day pass and single habitat day pass
      return {
        habitatId,
        primaryProductType: 'all-habitats-day-pass',
        secondaryProductType: 'single-habitat-day-pass',
        forEducator: isEducator,
      };
    },
  ),
);

export const routeToHabitat = (
  {
    id,
    slug,
    zooSlug,
    query,
  }: {
    id: string;
    slug: string;
    zooSlug: string;
    query?: string;
  },
  productsPromptModalState?: Parameters<typeof setProductsPromptModalState>[0],
) => {
  const state = store.getState();
  const canAccessHabitat = createSelectCanAccessHabitat(id)(state);
  const isLoggedIn = state.user.logged;
  if (!isLoggedIn) {
    store.dispatch(setMembersOnlyWarningModalState({ habitat: { id, slug, zooSlug }, type: 'habitat' }));
    return;
  }
  if (canAccessHabitat) {
    route(buildHabitatURL(zooSlug, slug, query));
    return;
  }
  const defaultState = createSelectProductsPromptModalState(id)(state);
  store.dispatch(setProductsPromptModalState({ ...defaultState, ...productsPromptModalState }));
};
